import React from 'react';
import PropTypes from 'prop-types';

/**
 * A redux-form radio choice shown as selectable cards. Options with an icon
 * get a full card (icon, label, optional hint); options without one get a
 * slimmer card. Each card wraps a real radio input, so it works from the
 * keyboard and for screen readers. `columns` sets how many go in a row
 * (default 2; always one on phones); `disabled` locks the whole choice, an
 * option's own `disabled` just that card.
 * Styles: .choice-cards in form-card.scss.
 */
const ChoiceCards = ({
  input,
  options,
  ariaLabel,
  onPick,
  columns,
  disabled,
}) => (
  <div
    className={`choice-cards${
      columns !== 2 ? ` choice-cards--cols-${columns}` : ''
    }`}
    role="radiogroup"
    aria-label={ariaLabel}
  >
    {options.map(({ value, icon: Icon, label, hint, disabled: locked }) => {
      const checked = input.value === value;
      const off = disabled || locked;
      return (
        <label
          key={value}
          className={`choice-card${checked ? ' choice-card--on' : ''}${
            Icon ? '' : ' choice-card--compact'
          }${off ? ' choice-card--disabled' : ''}`}
        >
          <input
            type="radio"
            name={input.name}
            value={value}
            checked={checked}
            disabled={off}
            onChange={() => {
              input.onChange(value);
              if (onPick) onPick(value);
            }}
          />
          {Icon && (
            <span className="choice-card__icon" aria-hidden>
              <Icon size={20} />
            </span>
          )}
          <span className="choice-card__text">
            <span className="choice-card__label">{label}</span>
            {hint && <span className="choice-card__hint">{hint}</span>}
          </span>
          <span className="choice-card__dot" aria-hidden />
        </label>
      );
    })}
  </div>
);

ChoiceCards.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onChange: PropTypes.func,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      hint: PropTypes.node,
      icon: PropTypes.elementType,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
  onPick: PropTypes.func,
  columns: PropTypes.oneOf([1, 2, 3]),
  disabled: PropTypes.bool,
};

ChoiceCards.defaultProps = {
  ariaLabel: undefined,
  onPick: undefined,
  columns: 2,
  disabled: false,
};

export default ChoiceCards;
